import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import { gameAPI } from '../../services/api';
import { useTelegram } from '../../hooks/useTelegram';
import GameCard from '../../components/GameCard/GameCard';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
`;

const Header = styled.div`
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(10,10,15,0.95);
  backdrop-filter: blur(20px);
  border-bottom: 0.5px solid rgba(255,255,255,0.06);
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255,255,255,0.06);
  border: 2px solid transparent;
  border-radius: 14px;
  padding: 13px 44px;
  font-size: 16px;
  color: #fff;
  transition: all 0.2s;
  &::placeholder { color: rgba(255,255,255,0.25); }
  &:focus { outline: none; border-color: #006FFF; background: rgba(0,111,255,0.06); }
`;

const IconL = styled.div`
  position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
  color: rgba(255,255,255,0.3); pointer-events: none;
`;

const ClearBtn = styled.button`
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,0.1); border: none; color: rgba(255,255,255,0.5); cursor: pointer;
  padding: 4px; border-radius: 50%; width: 24px; height: 24px;
  display: flex; align-items: center; justify-content: center;
  opacity: ${p => p.$show ? 1 : 0};
  transition: opacity 0.2s;
`;

const QuickRow = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  margin-top: 10px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const QuickChip = styled.button`
  flex-shrink: 0;
  padding: 7px 14px;
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.5);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  &:active { background: rgba(0,111,255,0.15); color: #4da3ff; }
`;

const Content = styled.div`
  padding: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Empty = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: rgba(255,255,255,0.3);
  h3 { color: rgba(255,255,255,0.6); font-size: 16px; margin-bottom: 8px; }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const Count = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.3);
  margin-bottom: 12px;
`;

const Skeleton = styled.div`
  background: linear-gradient(90deg, #14141f 25%, #1a1a2e 50%, #14141f 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 14px;
  aspect-ratio: 3/4;
`;

const QUICK = ['Spider-Man', 'God of War', 'FIFA', 'Call of Duty', 'GTA', 'Assassin\'s Creed', 'Cyberpunk', 'The Last of Us', 'Forza', 'Minecraft'];

const Search = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { tg } = useTelegram();
  const [query, setQuery] = useState(params.get('q') || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const goBack = () => navigate('/'); if (tg) { tg.setHeaderColor('#0a0a0f'); tg.BackButton.show(); tg.BackButton.onClick(goBack); } return () => { if (tg) { tg.BackButton.offClick(goBack); tg.BackButton.hide(); } };
  }, [tg, navigate]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim().length >= 2) {
        setSearching(true);
        gameAPI.search(query.trim()).then(r => { setResults(r); setSearching(false); }).catch(() => setSearching(false));
        setParams({ q: query });
      } else {
        setResults([]);
        if (query.length === 0) setParams({});
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <Page>
      <Header>
        <InputWrap>
          <Input placeholder="Поиск игр PlayStation..." value={query} onChange={e => setQuery(e.target.value)} autoFocus />
          <IconL><FiSearch size={18} /></IconL>
          <ClearBtn $show={query.length > 0} onClick={() => setQuery('')}><FiX size={14} /></ClearBtn>
        </InputWrap>
        <QuickRow>
          {QUICK.map(q => <QuickChip key={q} onClick={() => setQuery(q)}>{q}</QuickChip>)}
        </QuickRow>
      </Header>

      <Content>
        {query.trim().length >= 2 ? (
          <>
            {searching ? (
              <Grid>{[1,2,3,4].map(i => <Skeleton key={i} />)}</Grid>
            ) : results.length > 0 ? (
              <>
                <Count>Найдено: {results.length}</Count>
                <Grid>
                  {results.map(g => <GameCard key={g.id} game={g} />)}
                </Grid>
              </>
            ) : (
              <Empty>
                <EmptyIcon>🔍</EmptyIcon>
                <h3>Ничего не найдено</h3>
                <p>Попробуйте другой запрос</p>
              </Empty>
            )}
          </>
        ) : (
          <Empty>
            <EmptyIcon>🎮</EmptyIcon>
            <h3>Поиск игр PlayStation</h3>
            <p>Введите название игры, жанр или издателя</p>
          </Empty>
        )}
      </Content>
    </Page>
  );
};

export default Search;
